'use client'

import { forwardRef, useId, useRef } from 'react'

import { cn } from '@/core/utils/common'
import { useHydrated } from '@/core/hooks/use-hydrated'
import { formatNumber, formatPhone } from '@/core/utils/format'
import { fieldStyles } from './field-styles'

import type { ChangeEvent, CompositionEvent, InputHTMLAttributes } from 'react'

/** bare = 기본·변형 클래스 없이 호출부 className 만 쓴다. label · helperText · error 가 없으면 감싸는 div 도 두지 않는다 */
type InputVariant = 'outline' | 'underline' | 'bare'
type ValueType =
  'string' | 'date' | 'phone' | 'email' | 'number' | 'ea' | 'alphanumeric'

type InputTextProps = InputHTMLAttributes<HTMLInputElement> & {
  label?: string
  error?: string
  helperText?: string
  variant?: InputVariant
  valueType?: ValueType
  classNames?: {
    container?: string
    input?: string
    label?: string
    error?: string
    helperText?: string
  }
}

const variantStyles: Record<InputVariant, string> = {
  outline: fieldStyles.outline,
  underline:
    'h-12 border-b border-border bg-transparent px-1 focus:border-point',
  bare: '',
}

function filterValue(value: string, type: ValueType): string {
  switch (type) {
    case 'number':
      return value.replace(/[^0-9]/g, '')
    case 'phone':
      return formatPhone(value)
    case 'ea':
      return formatNumber(value)
    case 'alphanumeric':
      return value.replace(/[^a-zA-Z0-9]/g, '')
    case 'date':
      return value
        .replace(/[^0-9]/g, '')
        .replace(/^(\d{4})(\d)/, '$1-$2')
        .replace(/^(\d{4}-\d{2})(\d)/, '$1-$2')
        .slice(0, 10)
    case 'email':
      return value.replace(/[^a-zA-Z0-9@._-]/g, '')
    case 'string':
    default:
      return value
  }
}

export const InputText = forwardRef<HTMLInputElement, InputTextProps>(
  (
    {
      label,
      error,
      helperText,
      variant = 'outline',
      valueType = 'string',
      id,
      onChange,
      onCompositionStart,
      onCompositionEnd,
      className,
      'aria-describedby': externalDescription,
      'aria-invalid': externalInvalid,
      classNames,
      ...props
    },
    ref
  ) => {
    const generatedId = useId()
    const hydrated = useHydrated()
    const inputId = id ?? generatedId
    const helperTextId = helperText ? `${inputId}-helper` : undefined
    const errorId = error ? `${inputId}-error` : undefined
    const describedBy =
      [externalDescription, errorId ?? helperTextId]
        .filter(Boolean)
        .join(' ') || undefined
    const isComposingRef = useRef(false)

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      if (isComposingRef.current) {
        onChange?.(e)
        return
      }

      if (valueType !== 'string') {
        e.target.value = filterValue(e.target.value, valueType)
      }

      onChange?.(e)
    }

    const handleCompositionStart = (
      event: CompositionEvent<HTMLInputElement>
    ) => {
      isComposingRef.current = true
      onCompositionStart?.(event)
    }

    const handleCompositionEnd = (e: CompositionEvent<HTMLInputElement>) => {
      isComposingRef.current = false

      if (valueType !== 'string') {
        const input = e.target as HTMLInputElement
        const filtered = filterValue(input.value, valueType)

        if (input.value !== filtered) {
          input.value = filtered
          input.dispatchEvent(new Event('input', { bubbles: true }))
        }
      }
      onCompositionEnd?.(e)
    }

    const control = (
      <input
        ref={ref}
        id={inputId}
        aria-invalid={error ? true : externalInvalid}
        aria-describedby={describedBy}
        className={cn(
          variant !== 'bare' && fieldStyles.control,
          variantStyles[variant],
          error && 'border-error focus:border-error',
          classNames?.input,
          className
        )}
        onChange={handleChange}
        onCompositionStart={handleCompositionStart}
        onCompositionEnd={handleCompositionEnd}
        {...props}
        disabled={
          props.disabled || (!hydrated && typeof onChange === 'function')
        }
      />
    )

    if (variant === 'bare' && !label && !helperText && !error) return control

    return (
      <div className={cn(classNames?.container)}>
        {label && (
          <label
            htmlFor={inputId}
            className={cn(fieldStyles.label, classNames?.label)}
          >
            {label}
          </label>
        )}
        {control}
        {helperText && !error && (
          <p
            id={helperTextId}
            className={cn(fieldStyles.helper, classNames?.helperText)}
          >
            {helperText}
          </p>
        )}
        {error && (
          <p
            id={errorId}
            role="alert"
            className={cn(fieldStyles.error, classNames?.error)}
          >
            {error}
          </p>
        )}
      </div>
    )
  }
)

InputText.displayName = 'InputText'
